import React, { useEffect } from "react";
import { Navigate, Route, Routes } from "react-router-dom";

import HomePage from "./pages/HomePage";
import SignUpPage from "./pages/SignUpPage";
import LoginPage from "./pages/LoginPage";
import AdminPage from "./pages/AdminPage";
import CategoryPage from "./pages/CategoryPage";
import ProductPage from "./pages/ProductPage";
import AboutPage from "./pages/AboutPage";
import ContactPage from "./pages/ContactPage";
import ReturnsPolicyPage from "./pages/ReturnsPolicyPage";
import ShippingPolicyPage from "./pages/ShippingPolicyPage";
import TermsOfServicePage from "./pages/TermsOfServicePage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import WishlistPage from "./pages/WishlistPage";
import UserProfilePage from "./pages/UserProfilePage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import IsraeliPaymentPage from "./pages/IsraeliPaymentPage";
import HypaySuccessPage from "./pages/HypaySuccessPage";
import HypayFailurePage from "./pages/HypayFailurePage";
import CartPage from "./pages/CartPage";
import PurchaseSuccessPage from "./pages/PurchaseSuccessPage";
import PurchaseCancelPage from "./pages/PurchaseCancelPage";

import Navbar from "./components/Navbar";
import BottomNavigation from "./components/BottomNavigation";
import Footer from "./components/Footer";
import CookieConsent from "./components/CookieConsent";
import AccessibilityMenu from "./components/AccessibilityMenu";
import PWAInstallPrompt from "./components/PWAInstallPrompt";
import ScrollToTop from "./components/ScrollToTop";
import ScrollUpButton from "./components/ScrollUpButton";
import DeveloperNotificationPanel from "./components/DeveloperNotificationPanel";
import NotificationTestPanel from "./components/NotificationTestPanel";
import LoadingSpinner from "./components/LoadingSpinner";
import ErrorBoundary from "./components/ErrorBoundary";

import { Toaster } from "react-hot-toast";
import { useUserStore } from "./stores/useUserStore";
import { useCartStore } from "./stores/useCartStore";
import { ApiProvider } from "./components/ApiManager";

function App() {
	const { user, checkAuth, checkingAuth } = useUserStore();
	const { getCartItems, initializeCart } = useCartStore();

	useEffect(() => {
		// תמיד נבדוק אותנטיקציה; מנגנון הרענון יתפוס 401 אוטומטית
		checkAuth();
		
		// אתחל את הסל בעת טעינת האפליקציה
		initializeCart();
	}, [checkAuth, initializeCart]);

	useEffect(() => {
		if (user) {
			getCartItems();
		}
	}, [getCartItems, user]);

	if (checkingAuth) return <LoadingSpinner />;

	return (
		<ApiProvider>
			<ErrorBoundary>
				<div className="min-h-screen bg-gray-800 flex flex-col" dir="rtl">
					<ScrollToTop />
					<Navbar />
					<main className="flex-1 pb-16 md:pb-0">
						<ErrorBoundary>
							<Routes>
							<Route path="/" element={<HomePage />} />
							<Route path="/signup" element={!user ? <SignUpPage /> : <Navigate to="/" />} />
							<Route path="/login" element={!user ? <LoginPage /> : <Navigate to="/" />} />
							<Route path="/admin" element={user?.role === "admin" ? <AdminPage /> : <Navigate to="/login" />} />
							<Route path="/category/:category" element={<CategoryPage />} />
							<Route path="/product/:productId" element={<ProductPage />} />
						<Route path="/cart" element={<CartPage />} />
							<Route path="/about" element={<AboutPage />} />
							<Route path="/contact" element={<ContactPage />} />
							<Route path="/returns-policy" element={<ReturnsPolicyPage />} />
							<Route path="/shipping-policy" element={<ShippingPolicyPage />} />
							<Route path="/terms-of-service" element={<TermsOfServicePage />} />
							<Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
							<Route path="/wishlist" element={user ? <WishlistPage /> : <Navigate to="/login" />} />
							<Route path="/profile" element={user ? <UserProfilePage /> : <Navigate to="/login" />} />
							<Route path="/forgot-password" element={!user ? <ForgotPasswordPage /> : <Navigate to="/" />} />
							<Route path="/reset-password" element={!user ? <ResetPasswordPage /> : <Navigate to="/" />} />
							
							{/* 🇮🇱 כספית (Hypay) - התשלום הראשי לאתר */}
							<Route path="/payment" element={user ? <IsraeliPaymentPage /> : <Navigate to="/login" />} />
							
							{/* עמודי תשלום כספית */}
							<Route path="/payment/hypay/success" element={<HypaySuccessPage />} />
							<Route path="/payment/hypay/failure" element={<HypayFailurePage />} />
							
							{/* ✅ עמודים קיימים - נשמרים לתאימות לאחור */}
							<Route path="/israeli-payment" element={<IsraeliPaymentPage />} />
							<Route path="/purchase-success" element={user ? <PurchaseSuccessPage /> : <Navigate to="/login" />} />
							<Route path="/purchase-cancel" element={user ? <PurchaseCancelPage /> : <Navigate to="/login" />} />
						</Routes>
					</ErrorBoundary>
				</main>
				<BottomNavigation />
				<Footer />
				<CookieConsent />
				<AccessibilityMenu />
				<PWAInstallPrompt />
				<ScrollUpButton />
				{user?.role === 'admin' && (
					<>
						<DeveloperNotificationPanel />
						{import.meta.env.DEV && <NotificationTestPanel />}
					</>
				)}
				<Toaster position="top-center" />
			</div>
		</ErrorBoundary>
	</ApiProvider>
	);
}

export default App;
